<script setup lang="ts">
import dayjs from 'dayjs';
import FacebookIcon from '@/components/icons/FacebookIcon.vue';
import XIcon from '@/components/icons/XIcon.vue';
import InstagramIcon from '@/components/icons/InstagramIcon.vue';
import TiktokIcon from '@/components/icons/TiktokIcon.vue';
import YoutubeIcon from '@/components/icons/YoutubeIcon.vue';
import LinkedInIcon from '@/components/icons/LinkedInIcon.vue';

interface SocialIcon {
	[key: string]: any;
}

const socialIcons: SocialIcon = {
	facebook: FacebookIcon,
	x: XIcon,
	instagram: InstagramIcon,
	tiktok: TiktokIcon,
	youtube: YoutubeIcon,
	linkedin: LinkedInIcon,
};

const currentYear = dayjs().year();
const router = useRouter();

const featuresList = [
	{ name: 'Hosting events', url: getRouteUrl({ name: ROUTE.FEATURE_HOSTING_EVENTS.NAME }, router) },
	{ name: 'Invitations', url: getRouteUrl({ name: ROUTE.FEATURE_INVITATION.NAME }, router) },
	{ name: 'Pricing', url: getRouteUrl({ name: ROUTE.PRICING.NAME }, router) },
];

const companyList = [
	{ name: 'About us', url: getRouteUrl({ name: ROUTE.ABOUT_US.NAME }, router) },
	{ name: 'Contact', url: 'https://us-help-organizer.peatix.com/support/tickets/new' },
];

const resourcesList = [
	{ name: 'Blog', url: 'https://stories.peatix.com' },
	{ name: 'Help center', url: getRouteUrl({ name: ROUTE.HELP.NAME }, router) },
];

const legalList = [{ name: 'Terms', url: getRouteUrl({ name: ROUTE.TERMS.NAME }, router) }];

const socialList = [
	{ name: 'instagram', url: 'https://www.instagram.com/peatix' },
	{ name: 'tiktok', url: 'https://www.tiktok.com/@peatix' },
	{ name: 'x', url: 'https://x.com/Peatix' },
	{ name: 'linkedin', url: 'https://www.linkedin.com/company/peatix' },
	{ name: 'youtube', url: 'https://www.youtube.com/@peatix' },
	{ name: 'facebook', url: 'https://www.facebook.com/Peatix' },
];
</script>

<template>
	<div class="footer-container">
		<div class="upper-container">
			<div class="features-group group-container">
				<h6>Features</h6>
				<div class="group-links">
					<template v-for="link in featuresList" :key="link.name">
						<a :href="link.url" target="_blank" rel="noopener noreferrer">
							<BaseButton class="link" variant="link" color="gray">
								{{ link.name }}
							</BaseButton>
						</a>
					</template>
				</div>
			</div>
			<div class="company-group group-container">
				<h6>Company</h6>
				<div class="group-links">
					<template v-for="link in companyList" :key="link.name">
						<a :href="link.url" target="_blank" rel="noopener noreferrer">
							<BaseButton class="link" variant="link" color="gray">
								{{ link.name }}
							</BaseButton>
						</a>
					</template>
				</div>
			</div>
			<div class="resources-group group-container">
				<h6>Resources</h6>
				<div class="group-links">
					<template v-for="link in resourcesList" :key="link.name">
						<a :href="link.url" target="_blank" rel="noopener noreferrer">
							<BaseButton class="link" variant="link" color="gray">
								{{ link.name }}
							</BaseButton>
						</a>
					</template>
				</div>
			</div>
			<div class="legal-group group-container">
				<h6>Legal</h6>
				<div class="group-links">
					<template v-for="link in legalList" :key="link.name">
						<a :href="link.url" target="_blank" rel="noopener noreferrer">
							<BaseButton class="link" variant="link" color="gray">
								{{ link.name }}
							</BaseButton>
						</a>
					</template>
				</div>
			</div>
		</div>

		<VDivider />

		<div class="lower-container">
			<span class="copyright">© {{ currentYear }} Peatix</span>
			<div class="social-links">
				<template v-for="btn in socialList" :key="btn.name">
					<a :href="btn.url" target="_blank" rel="noopener noreferrer">
						<BaseButton class="link" variant="link" color="tertiary-gray">
							<component :is="socialIcons[btn.name]" width="24" height="24" />
						</BaseButton>
					</a>
				</template>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.footer-container {
	width: 100%;
	max-width: rem(1008);
	display: flex;
	flex-direction: column;
	gap: spacings-get(8);
	padding: rem(64) spacings-get(4) rem(48);
	margin: 0 auto;

	@include media-query-max(tablet) {
		padding: rem(48) spacings-get(4);
	}
}

.upper-container {
	display: grid;
	grid-template-areas: 'features company resources legal';
	grid-template-columns: repeat(4, 1fr);
	gap: spacings-get(8);

	@include media-query-max(tablet) {
		grid-template-areas:
			'features company'
			'resources .'
			'legal .';
		grid-template-columns: repeat(2, 1fr);
	}

	.group-container {
		display: flex;
		flex-direction: column;
		gap: spacings-get(4);

		h6 {
			@include fonts-get(semibold, text-sm);
			color: colors-get(gray, 500);
		}

		.group-links {
			display: flex;
			flex-direction: column;
			gap: spacings-get(3);
		}
	}

	.features-group {
		grid-area: features;
	}

	.company-group {
		grid-area: company;
	}

	.resources-group {
		grid-area: resources;
	}

	.legal-group {
		grid-area: legal;
	}
}

.lower-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: spacings-get(8);
	padding: 0;

	@include media-query-max(tablet) {
		flex-direction: column-reverse;
		align-items: flex-start;
		gap: spacings-get(6);
	}

	.copyright {
		@include fonts-get(regular, text-md);
		color: colors-get(gray, 500);
	}

	.social-links {
		display: flex;
		gap: spacings-get(6);
	}
}

.link {
	padding: 0;
}
</style>
